import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { DonDto, IncidentDto, IncidentPhotoDto, TypeIncidentDto, VisiteDto } from "src/api/models";
import { IncidentsService, TypesIncidentsService, VisitesService } from "src/api/services";
import { Visites } from "./visites.actions";
import { VisitesState, VisitesStateDefault } from "./visites.state";
import { HttpErrorResponse } from "@angular/common/http";

@State<VisitesState>({
  name: 'visites',
  defaults: VisitesStateDefault,
})
@Injectable()
export class VisitesStore {

  constructor(
    private visitesService: VisitesService,
    private typesIncidentsService: TypesIncidentsService,
    private incidentsService: IncidentsService
    ) {
  }

  @Selector()
  static visite$(state: VisitesState) {
    return state.visite;
  }

  @Selector()
  static photosIncident$(state: VisitesState) {
    return state.photosIncident;
  }

  @Selector()
  static typesIncidents$(state: VisitesState) {
    return state.typesIncidents;
  }

  @Selector()
  static incident$(state:VisitesState) {
    return state.incident;
  }

  @Action(Visites.GetDetail)
  getDetail( { patchState }: StateContext<VisitesState>, { visiteId }: Visites.GetDetail) {
    return this.visitesService.visiteGet({ id: visiteId }).pipe(
      tap( (res: VisiteDto) => {
        patchState({
          visite: res
        });
      }),
      catchError( (error: Error) => {
        patchState({
          visite: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Visites.UpdateVisite)
  updateVisite( {patchState}: StateContext<VisitesState>, { visite }: Visites.UpdateVisite) {
    return this.visitesService.visitePut( { id: visite.Id, body: visite }).pipe(
      tap( (res: VisiteDto) => {
        patchState({
          visite: res,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          visite: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Visites.UpdateDon)
  updateDon( {patchState}: StateContext<VisitesState>, { don }: Visites.UpdateDon) {
    return this.visitesService.visiteUpdateDonnerie({ body: don }).pipe(
      tap( (res: DonDto) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Visites.GetIncidentPhotos)
  getIncidentPhotos( { patchState }:StateContext<VisitesState>, { incidentId }: Visites.GetIncidentPhotos) {
    return this.incidentsService.incidentGetPhotos( { id: incidentId}).pipe(
      tap( (res: IncidentPhotoDto[]) => {
        patchState({
          photosIncident: res
        });
      }),
      catchError( (error: Error) => {
        patchState({
          photosIncident: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Visites.GetTypesIncidents)
  getTypesIncidents( { patchState}:StateContext<VisitesState>) {
    return this.typesIncidentsService.typeIncidentGetList().pipe(
      tap( (res: TypeIncidentDto[]) => {
        patchState({
          typesIncidents: res,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          typesIncidents: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Visites.CreateIncident)
  createIncident( {patchState}: StateContext<VisitesState>, { incident }: Visites.CreateIncident) {
    return this.incidentsService.incidentPost( { body: incident }).pipe(
      tap( (res: IncidentDto) => {
        patchState({
          incident: res,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          incident: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Visites.UpdateIncident)
  updateIncident( {patchState}: StateContext<VisitesState>, { incidentId, incident }: Visites.UpdateIncident) {
    return this.incidentsService.incidentPut( { id: incidentId, body: incident }).pipe(
      tap( (res: IncidentDto) => {
        patchState({
          incident: res,
          error: null
        });
      }),
      catchError( (error: Error) => {
        patchState({
          incident: null,
          error: error.message
        });
        return of(error);
      })
    );
  }

  @Action(Visites.PrepareIncident)
  prepareIncident( {patchState}: StateContext<VisitesState>, { incident }: Visites.PrepareIncident) {
    patchState({
      incident: incident
    });
  }

  @Action(Visites.AddIncidentPhotoPDA)
  addIncidentPhotoPDA({patchState}: StateContext<VisitesState>, {incidentId, photoPDAIds}: Visites.AddIncidentPhotoPDA){
    return this.incidentsService.incidentAddIncidentPhotoPda({id: incidentId, body: photoPDAIds}).pipe(
      tap((res: IncidentDto) => {
        patchState({
          error: null
        });
      }),
      catchError( (error: HttpErrorResponse) => {
        patchState({
          error: error.error
        });
        return of(error);
      })
    );
  }

}
